/**
 * AST 기반 자동 생성된 검증 함수
 * 이 파일은 scripts/generate-validators.ts에 의해 자동 생성됩니다.
 * 수동으로 수정하지 마세요.
 * 
 * 생성 시간: 2026-10-02T04:29:21.357Z
 * 소스 파일: src/data/dto/user/create-temp-user-response-dto
 */

import type { CreateTempUserResponseDto } from "@/data/dto/user/create-temp-user-response-dto";

/**
 * CreateTempUserResponseDto 타입 가드 함수
 * AST 분석을 통해 자동 생성된 최적화된 검증 함수
 */
export function isCreateTempUserResponseDto(input: unknown): input is CreateTempUserResponseDto {
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    return false;
  }
  
  const obj = input as Record<string, unknown>;
  
  return (
    typeof obj.userId === "number" && !isNaN(obj.userId) &&
    typeof obj.userUuid === "string" &&
    true &&
    (obj.screeningId === undefined || typeof obj.screeningId === "string") &&
    (obj.loginId === undefined || typeof obj.loginId === "string") &&
    (obj.hospitalCode === undefined || typeof obj.hospitalCode === "string")
  );
}

/**
 * CreateTempUserResponseDto 상세 검증 결과 반환 함수
 * 검증 실패 시 상세한 에러 정보를 반환합니다.
 */
export interface IValidation<T> {
  success: boolean;
  data?: T;
  errors?: Array<{ path: string; expected: string; value: unknown }>;
}

export function validateCreateTempUserResponseDto(input: unknown): IValidation<CreateTempUserResponseDto> {
  const errors: Array<{ path: string; expected: string; value: unknown }> = [];
  
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    errors.push({
      path: "",
      expected: "object",
      value: input,
    });
    return {
      success: false,
      errors,
    };
  }
  
  const obj = input as Record<string, unknown>;
  
    if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
      errors.push({ path: "", expected: "object", value: obj });
    } else {
      const objObj = obj as Record<string, unknown>;
      // userId (required)
      if (objObj.userId === undefined) {
        errors.push({ path: "userId", expected: "number", value: undefined });
      } else {
        if (!(typeof objObj.userId === "number" && !isNaN(objObj.userId))) {
          errors.push({ path: "userId", expected: "number", value: objObj.userId });
        }
      }
      // userUuid (required)
      if (objObj.userUuid === undefined) {
        errors.push({ path: "userUuid", expected: "string", value: undefined });
      } else {
        if (typeof objObj.userUuid !== "string") {
          errors.push({ path: "userUuid", expected: "string", value: objObj.userUuid });
        }
      }
      // role (required)
      if (objObj.role === undefined) {
        errors.push({ path: "role", expected: "unknown", value: undefined });
      } else {
        // Unknown type: runtime validation is not generated.
      }
      // screeningId (optional)
      if (objObj.screeningId !== undefined) {
        if (typeof objObj.screeningId !== "string") {
          errors.push({ path: "screeningId", expected: "string", value: objObj.screeningId });
        }
      }
      // loginId (optional)
      if (objObj.loginId !== undefined) {
        if (typeof objObj.loginId !== "string") {
          errors.push({ path: "loginId", expected: "string", value: objObj.loginId });
        }
      }
      // hospitalCode (optional)
      if (objObj.hospitalCode !== undefined) {
        if (typeof objObj.hospitalCode !== "string") {
          errors.push({ path: "hospitalCode", expected: "string", value: objObj.hospitalCode });
        }
      }
    }
  
  if (errors.length === 0 && isCreateTempUserResponseDto(input)) {
    return {
      success: true,
      data: input,
    };
  }
  
  return {
    success: false,
    errors: errors.length > 0 ? errors : [
      {
        path: "",
        expected: "CreateTempUserResponseDto",
        value: input,
      },
    ],
  };
}

/**
 * CreateTempUserResponseDto 검증 후 에러 throw 함수
 * 검증 실패 시 상세한 에러 정보를 포함하여 에러를 throw합니다.
 */
export function assertCreateTempUserResponseDto(input: unknown): CreateTempUserResponseDto {
  const validation = validateCreateTempUserResponseDto(input);
  if (!validation.success) {
    const errorDetails = validation.errors?.map(err => 
      `  - ${err.path}: 예상 타입 ${err.expected}, 실제 값: ${JSON.stringify(err.value)}`
    ).join("\n") || "알 수 없는 오류";
    throw new Error(`[assertCreateTempUserResponseDto] :: 검증 실패: CreateTempUserResponseDto 타입과 일치하지 않습니다\n\n오류 상세:\n${errorDetails}`);
  }
  return validation.data!;
}
